import { useAtom, useAtomValue } from 'jotai';
import { useStore } from 'jotai/react';
import { useCallback, useRef } from 'react';
import { useAltDragCopy } from '@/hooks/board/useAltDragCopy';
import { useShiftDragConstraint } from '@/hooks/board/useShiftDragConstraint';
import { draftSelectionIdsAtom, movingElementsIdsAtom, presentationPositionAtom, presentationViewChangedAtom, selectedIdsAtom, snapToGuidesAtom, stageModeAtom, stageRefAtom, stageViewportAtom, } from '@/store/boardAtoms';
import { prioritizeCacheForVisibleElements } from '@/utils/board/imageCacheUtils';
import { clearDragInitialPositions, saveDragInitialPositions } from '@/utils/board/insertionUtils';
import { applySnapping, applySnappingToMultipleElements, clearGuides, drawGuides, getGuides, getLineGuideStops, getMultipleElementsBoundingBox, getMultipleElementsSnappingEdges, getObjectSnappingEdges, isInsideAnyLocalGrid, } from '@/utils/board/snappingHelpers';
import { draftStateAfterUpdate, getElementPositionFromNode, getElementsMap, getNodeOffset, isElementShape, } from '@/utils/board/stageUtils';
/**
 * Кастомный хук для обработки событий перетаскивания на Stage
 *
 * @remarks
 * Хук обрабатывает:
 * - Перетаскивание самой сцены (панорамирование)
 * - Перетаскивание элементов с поддержкой снаппинга к гайдлайнам
 * - Копирование элементов при перетаскивании с Alt
 * - Ограничение перемещения по оси при зажатом Shift
 * - Синхронизацию позиции в режиме презентации
 *
 * @param params - Параметры хука
 * @returns Объект с обработчиками событий и ref'ами для отслеживания состояния
 */
export const useStageDragEvents = ({ boardState, setBoardState, canEdit, visibleElements: _visibleElements, send, getElementClickInfo, setPosition: setPositionParam, isPresentationMode = false, presentationOffsets, }) => {
    // Store для синхронного чтения атомов вне React-контекста
    const store = useStore();
    // Атомы состояния
    const [selectedIds, setSelectedIds] = useAtom(selectedIdsAtom);
    const [draftSelectionIds, setDraftSelectionIds] = useAtom(draftSelectionIdsAtom);
    const [stageMode] = useAtom(stageModeAtom);
    const [, setPresentationPosition] = useAtom(presentationPositionAtom);
    const [, setPresentationViewChanged] = useAtom(presentationViewChangedAtom);
    const [snapToGuides] = useAtom(snapToGuidesAtom);
    const [, setMovingElementsIds] = useAtom(movingElementsIdsAtom);
    // Получаем setPosition из параметра или из атома
    const [, setViewport] = useAtom(stageViewportAtom);
    const setPositionFromAtom = useCallback((position) => {
        setViewport((prev) => {
            const newPosition = typeof position === 'function' ? position(prev.position) : position;
            return Object.assign(Object.assign({}, prev), { position: newPosition });
        });
    }, [setViewport]);
    const setPosition = setPositionParam || setPositionFromAtom;
    // Рефы для отслеживания состояния перетаскивания
    const elementMoveId = useRef(null);
    const currentMoveElementsIds = useRef([]);
    // Хук для обработки копирования с Alt
    // Получаем viewport из атома напрямую (без ререндеров, так как используется только в callbacks)
    const viewport = useAtomValue(stageViewportAtom);
    const zoom = viewport.zoom;
    const altDragCopy = useAltDragCopy({
        boardState,
        send,
        zoom,
    });
    // Хук для ограничения перемещения по оси с Shift
    const shiftDragConstraint = useShiftDragConstraint();
    /**
     * Обработчик начала перетаскивания Stage
     *
     * @remarks
     * Обрабатывает начало перетаскивания:
     * - Сцены (панорамирование)
     * - Элементов (перемещение)
     *
     * При начале перетаскивания элемента:
     * - Сохраняет ID перемещаемого элемента
     * - Инициализирует копирование с Alt (если зажат Alt)
     * - Инициализирует ограничение перемещения с Shift
     * - Приоритизирует кэширование видимых изображений
     */
    const onStageDragStart = useCallback((e) => {
        const stage = store.get(stageRefAtom);
        if (e.target === stage) {
            // console.log('onStageDragStart');
            // В режиме презентации выделение запрещено
            if (!isPresentationMode && selectedIds.length) {
                setDraftSelectionIds([...selectedIds]);
                setSelectedIds([]);
            }
            // Приоритизируем кэширование видимых изображений при начале панорамирования
            const stage = store.get(stageRefAtom);
            if (stage) {
                prioritizeCacheForVisibleElements(stage, viewport.zoom, viewport.size.width, viewport.size.height);
            }
        }
        else if (isElementShape(e.target)) {
            // console.log('onStageDragStart ~ ELEMENT');
            const { element, isLockedElement, layerLocked } = getElementClickInfo(e);
            if (!element || isLockedElement || layerLocked)
                return;
            // Очищаем предыдущие начальные позиции (на случай, если предыдущее перетаскивание не завершилось)
            clearDragInitialPositions();
            elementMoveId.current = element.id;
            currentMoveElementsIds.current = selectedIds.includes(element.id)
                ? selectedIds
                : [element.id];
            // Сохраняем начальные позиции всех элементов для синхронизации снаппинга
            const stage = store.get(stageRefAtom);
            if (stage && currentMoveElementsIds.current.length > 1) {
                saveDragInitialPositions(stage, currentMoveElementsIds.current, element.id);
            }
            // Обновляем атом для отслеживания перемещаемых элементов (для скрытия иконок)
            setMovingElementsIds([...currentMoveElementsIds.current]);
            // Обработка копирования с Alt
            altDragCopy.handleDragStart(e, element, currentMoveElementsIds.current);
            // Обработка ограничения перемещения с Shift
            shiftDragConstraint.handleDragStart(e, e.target);
            // В режиме презентации выделение запрещено
            if (!isPresentationMode && selectedIds) {
                if (selectedIds.includes(element.id)) {
                    setDraftSelectionIds([...selectedIds]);
                    setSelectedIds([]);
                }
                else {
                    setDraftSelectionIds([element.id]);
                }
            }
        }
    }, [
        store,
        isPresentationMode,
        selectedIds,
        setDraftSelectionIds,
        setSelectedIds,
        viewport.zoom,
        viewport.size.width,
        viewport.size.height,
        getElementClickInfo,
        setMovingElementsIds,
        altDragCopy,
        shiftDragConstraint,
    ]);
    /**
     * Получает узлы Konva по их ID из сцены
     *
     * @param stage - Сцена Konva
     * @param elementIds - Массив ID элементов
     * @returns Массив найденных узлов и массив Shape для исключения из снаппинга
     */
    const getNodesByIds = useCallback((stage, elementIds) => {
        const nodes = [];
        const shapes = [];
        for (const id of elementIds) {
            const node = stage.findOne(`#node-${id}`);
            if (node) {
                nodes.push(node);
                shapes.push(node);
            }
        }
        return { nodes, shapes };
    }, []);
    /**
     * Вычисляет прямоугольную зону поиска для снаппинга
     *
     * @param movingBox - Bounding box перемещаемого элемента или группы
     * @param shiftKey - Зажата ли клавиша Shift (для увеличения зоны)
     * @returns Прямоугольная зона поиска и максимальное расстояние для снаппинга
     */
    const calculateProximityZone = useCallback((movingBox, shiftKey) => {
        const BASE_ZONE_MULTIPLIER = 2;
        const zoneMultiplier = shiftKey ? BASE_ZONE_MULTIPLIER * 1.5 : BASE_ZONE_MULTIPLIER;
        const centerX = movingBox.x + movingBox.width / 2;
        const centerY = movingBox.y + movingBox.height / 2;
        const zoneWidth = movingBox.width * zoneMultiplier;
        const zoneHeight = movingBox.height * zoneMultiplier;
        const proximityRect = {
            x: centerX - zoneWidth / 2,
            y: centerY - zoneHeight / 2,
            width: zoneWidth,
            height: zoneHeight,
        };
        const MAX_NEAR_DISTANCE = 150;
        const maxDistance = shiftKey ? MAX_NEAR_DISTANCE * 1.5 : MAX_NEAR_DISTANCE;
        return { proximityRect, maxDistance };
    }, []);
    /**
     * Обработчик снаппинга элемента при перетаскивании
     *
     * @remarks
     * Вычисляет направляющие линии на основе близлежащих элементов
     * и применяет снаппинг к позиции перетаскиваемого элемента.
     * Если элемент находится внутри элемента с локальной сеткой,
     * снаппинг по гайдлайнам отключается.
     * Поддерживает снаппинг для одного или нескольких элементов.
     *
     * @param e - Событие перетаскивания
     * @param layer - Слой Konva, на котором находится элемент
     * @param elementIds - Массив ID перемещаемых элементов (если несколько)
     */
    const handleElementSnapping = useCallback((e, layer, elementIds) => {
        var _a;
        // Очищаем предыдущие направляющие
        clearGuides(layer);
        // Если снаппинг по гайдлиниям выключен, не выполняем обработку
        if (!snapToGuides) {
            return;
        }
        // Используем основную сцену из stageRef вместо target.getStage()
        const mainStage = store.get(stageRefAtom);
        if (!mainStage)
            return;
        const target = e.target;
        const isMultiple = elementIds && elementIds.length > 1;
        // Если элемент внутри элемента с локальной сеткой — отключаем снаппинг по гайдлайнам
        const isInsideLocalGrid = isInsideAnyLocalGrid(mainStage, target, boardState);
        if (isInsideLocalGrid) {
            // Сетка этого контейнера должна обрабатывать свою привязку; гайдлайны не рисуем и не применяем
            clearGuides(layer);
            return;
        }
        // Получаем узлы и bounding box для одного или нескольких элементов
        let nodes;
        let skipShapes;
        let movingBox;
        if (isMultiple && elementIds) {
            // Для нескольких элементов получаем все узлы один раз
            const result = getNodesByIds(mainStage, elementIds);
            nodes = result.nodes;
            skipShapes = result.shapes;
            if (nodes.length === 0)
                return;
            // Получаем общий bounding box
            const combinedBox = getMultipleElementsBoundingBox(nodes);
            if (!combinedBox)
                return;
            movingBox = combinedBox;
        }
        else {
            // Для одного элемента используем стандартный метод
            nodes = [target];
            skipShapes = [target];
            movingBox = target.getClientRect({ skipStroke: true });
        }
        // Вычисляем зону поиска для снаппинга
        const shiftKey = Boolean((_a = e === null || e === void 0 ? void 0 : e.evt) === null || _a === void 0 ? void 0 : _a.shiftKey);
        const { proximityRect, maxDistance } = calculateProximityZone(movingBox, shiftKey);
        // Получаем точки снаппинга сцены, исключая все перемещаемые элементы
        const lineGuideStops = getLineGuideStops(mainStage, skipShapes.length > 1 ? skipShapes : skipShapes[0], false, proximityRect);
        // Получаем точки снаппинга текущего элемента или группы элементов
        const itemSnappingEdges = isMultiple
            ? getMultipleElementsSnappingEdges(nodes)
            : getObjectSnappingEdges(target);
        // Находим направляющие линии
        const guides = getGuides({
            event: e,
            lineGuideStops,
            itemSnappingEdges,
            stage: mainStage,
            linesNearDistance: maxDistance,
        });
        // Если нет направляющих, ничего не делаем
        if (!guides.length)
            return;
        // Отрисовываем направляющие
        drawGuides(guides, layer);
        // Применяем снаппинг
        if (isMultiple) {
            // Для нескольких элементов применяем снаппинг к общему bounding box
            applySnappingToMultipleElements(nodes, guides, movingBox);
        }
        else {
            // Для одного элемента применяем снаппинг к самому элементу
            applySnapping(target, guides);
        }
    }, [boardState, store, snapToGuides, getNodesByIds, calculateProximityZone]);
    /**
     * Обработчик движения во время перетаскивания Stage
     *
     * @remarks
     * Обрабатывает движение во время перетаскивания:
     * - Сцены: обновляет позицию в режиме презентации
     * - Элемента: применяет снаппинг, копирование с Alt, ограничение с Shift
     */
    const onStageDragMove = useCallback((e) => {
        var _a, _b;
        const stage = store.get(stageRefAtom);
        if (e.target === stage) {
            // Обработка перетаскивания самой сцены
            // console.log('onStageDragMove');
            if (isPresentationMode) {
                const stage = store.get(stageRefAtom);
                if (!stage)
                    return;
                const offX = (_a = presentationOffsets === null || presentationOffsets === void 0 ? void 0 : presentationOffsets.offsetX) !== null && _a !== void 0 ? _a : 0;
                const offY = (_b = presentationOffsets === null || presentationOffsets === void 0 ? void 0 : presentationOffsets.offsetY) !== null && _b !== void 0 ? _b : 0;
                // Переводим позицию Stage в координаты презентации
                setPresentationPosition({ x: stage.x() - offX, y: stage.y() - offY });
                setPresentationViewChanged(true);
            }
            return;
        }
        if (isElementShape(e.target)) {
            // console.log('onStageDragMove ~ ELEMENT');
            // Обработка перетаскивания элемента с снаппингом
            const layer = e.target.getLayer();
            if (!layer)
                return;
            // Обработка копирования с Alt
            altDragCopy.handleDragMove(e, e.target, currentMoveElementsIds.current);
            // Обработка ограничения перемещения с Shift
            shiftDragConstraint.handleDragMove(e, e.target);
            // Обычное перетаскивание с снаппингом (для одного или нескольких элементов)
            handleElementSnapping(e, layer, currentMoveElementsIds.current);
        }
    }, [
        handleElementSnapping,
        store,
        isPresentationMode,
        presentationOffsets,
        setPresentationPosition,
        setPresentationViewChanged,
        altDragCopy,
        shiftDragConstraint,
    ]);
    /**
     * Обработчик окончания перетаскивания Stage
     *
     * @remarks
     * Обрабатывает окончание перетаскивания:
     * - Сцены: сохраняет новую позицию
     * - Элемента: вычисляет финальные позиции всех перемещенных элементов,
     *   обновляет состояние и отправляет изменения на сервер
     */
    const onStageDragEnd = useCallback((e) => {
        var _a, _b, _c, _d;
        const stage = store.get(stageRefAtom);
        if (e.target === stage) {
            // console.log('onStageDragEnd');
            // Сохраняем позицию стейджа
            if (isPresentationMode) {
                // В режиме презентации фиксируем, что вид изменился
                const offX = (_a = presentationOffsets === null || presentationOffsets === void 0 ? void 0 : presentationOffsets.offsetX) !== null && _a !== void 0 ? _a : 0;
                const offY = (_b = presentationOffsets === null || presentationOffsets === void 0 ? void 0 : presentationOffsets.offsetY) !== null && _b !== void 0 ? _b : 0;
                setPresentationPosition({ x: e.target.x() - offX, y: e.target.y() - offY });
                setPresentationViewChanged(true);
            }
            else {
                // Используем единый стейт для обычного режима
                setPosition({ x: e.target.x(), y: e.target.y() });
            }
            // Возвращаем выделение из draftSelectionIds обратно в selectedIds после панорамирования
            // В режиме презентации выделение запрещено
            if (!isPresentationMode && draftSelectionIds.length > 0) {
                setSelectedIds([...draftSelectionIds]);
                setDraftSelectionIds([]);
            }
        }
        else if (isElementShape(e.target)) {
            // console.log('onStageDragEnd ~ ELEMENT');
            // Очищаем направляющие линии
            const layer = e.target.getLayer();
            if (layer) {
                clearGuides(layer);
            }
            const { element, isLockedElement, layerLocked } = getElementClickInfo(e);
            elementMoveId.current = null;
            // Очищаем атом для отслеживания перемещаемых элементов
            setMovingElementsIds([]);
            // Очищаем сохраненные начальные позиции
            clearDragInitialPositions();
            // Очистка ограничений перемещения с Shift
            shiftDragConstraint.handleDragEnd(e.target);
            if (!isPresentationMode && stageMode !== 'select')
                return;
            if (!canEdit || !element || isLockedElement || layerLocked)
                return;
            // Обработка копирования с Alt
            const wasCopying = altDragCopy.handleDragEnd(e, currentMoveElementsIds.current);
            if (wasCopying) {
                setDraftSelectionIds([]);
                setSelectedIds([]);
                currentMoveElementsIds.current = [];
                return;
            }
            // Обычное перемещение элементов
            // Собираем новые позиции для всех перемещаемых элементов
            const stage = store.get(stageRefAtom);
            if (!stage)
                return;
            const updates = [];
            const elementsMap = getElementsMap(boardState.layers);
            for (const id of currentMoveElementsIds.current) {
                const node = stage.findOne(`#node-${id}`);
                if (!node)
                    continue;
                // Получаем элемент из состояния доски
                const elementData = elementsMap.get(id);
                const elW = ((_c = elementData === null || elementData === void 0 ? void 0 : elementData.el.props) === null || _c === void 0 ? void 0 : _c.w) || 64;
                const elH = ((_d = elementData === null || elementData === void 0 ? void 0 : elementData.el.props) === null || _d === void 0 ? void 0 : _d.h) || 64;
                // Получаем offset из узла или вычисляем на основе размеров элемента
                const { offsetX, offsetY } = getNodeOffset(node, elW, elH);
                // ВАЖНО: После снаппинга к гайдлиниям позиция устанавливается через absolutePosition()
                // Поэтому нужно использовать absolutePosition() для получения точной позиции после снаппинга,
                // а не node.x() и node.y(), которые могут давать неточность из-за преобразования координат
                const absPos = node.absolutePosition();
                const scale = stage.scaleX();
                const stageX = stage.x();
                const stageY = stage.y();
                // Преобразуем абсолютную позицию (экранные координаты узла с offset) в world координаты
                const worldNodeX = (absPos.x - stageX) / scale;
                const worldNodeY = (absPos.y - stageY) / scale;
                // Вычитаем offset, чтобы получить реальную позицию элемента (левый верхний угол)
                const { x, y } = getElementPositionFromNode(worldNodeX, worldNodeY, offsetX, offsetY);
                // updates.push({ id, x: node.x(), y: node.y(), zIndex: elementZIndex });
                updates.push({ id, x, y });
            }
            setBoardState((prev) => draftStateAfterUpdate(prev, updates.map(({ id, x, y }) => ({
                id,
                props: { x, y },
            }))));
            // Отправляем все изменения разом
            send({ type: 'element.move', payload: updates });
            // Возвращаем выделение после перетаскивания
            if (draftSelectionIds.length === 1) {
                setDraftSelectionIds([]);
            }
            else {
                setSelectedIds([...draftSelectionIds]);
                setDraftSelectionIds([]);
            }
            currentMoveElementsIds.current = [];
        }
    }, [
        store,
        isPresentationMode,
        presentationOffsets === null || presentationOffsets === void 0 ? void 0 : presentationOffsets.offsetX,
        presentationOffsets === null || presentationOffsets === void 0 ? void 0 : presentationOffsets.offsetY,
        setPresentationPosition,
        setPresentationViewChanged,
        setPosition,
        getElementClickInfo,
        stageMode,
        canEdit,
        boardState.layers,
        setBoardState,
        send,
        draftSelectionIds,
        setDraftSelectionIds,
        setSelectedIds,
        altDragCopy,
        shiftDragConstraint,
        setMovingElementsIds,
    ]);
    return {
        onStageDragStart,
        onStageDragMove,
        onStageDragEnd,
        elementMoveId,
        currentMoveElementsIds,
    };
};
